import {
  ChevronsUpDown,
  Copy,
  Headset,
  LogOut,
  Settings,
} from "lucide-react";

import { useDispatch } from "react-redux";
import { useNavigate } from "react-router";
import { toast } from "sonner";
import { logout } from "@/features/auth/store/sessionSlice";
import { ThemeSwitcher } from "@/shared/components/ThemeSwitcher";
import { USER_SETTINGS_PATH } from "@/shared/configs/routes.config";
import { useHelpEntryPath } from "@/shared/hooks/useHelpCenterEnabled";
import { useOrganizationFeatures } from "@/shared/hooks/useOrganizationFeatures";
import { useRepresentativePermissions } from "@/shared/hooks/useRepresentativePermissions";
import { Avatar, AvatarFallback, AvatarImage } from "@/shared/ui/Avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/shared/ui/DropDownMenu";
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@/shared/ui/Sidebar";

type UserBadgeProps = {
  user: {
    firstName: string
    lastName: string
    email: string
    avatarUrl: string | undefined
    roleName: string | undefined
  }
};

const UserBadge = ({ user }: UserBadgeProps) => {
  const { isMobile, state } = useSidebar();
  const collapsed = state === "collapsed";
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const helpEntryPath = useHelpEntryPath();
  const { servicePreferences, serviceRepresentativeRoles } = useOrganizationFeatures();
  const { canReadRoles } = useRepresentativePermissions();
  const {
    firstName,
    lastName,
    email,
    avatarUrl,
    roleName,
  } = user;

  const roleLabel: string | null = serviceRepresentativeRoles && canReadRoles && roleName ? `Role: ${roleName}` : null;

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton
              size="lg"
              className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground cursor-pointer bg-sidebar-accent hover:bg-ghost/10 rounded-md"
            >
              <Avatar className="h-8 w-8 rounded-lg">
                <AvatarImage src={avatarUrl} alt={`${firstName} ${lastName}`} />
                <AvatarFallback className="rounded-lg">
                  {firstName?.[0]}
                  {lastName?.[0]}
                </AvatarFallback>
              </Avatar>
              {!collapsed && (
                <>
                  <div className="grid flex-1 text-left text-sm leading-tight">
                    <span className="truncate font-medium">{`${firstName} ${lastName}`}</span>
                    <span className="truncate text-xs">{email}</span>
                  </div>
                  <ChevronsUpDown className="ml-auto size-4" />
                </>
              )}
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className="w-(--radix-dropdown-menu-trigger-width) min-w-56 rounded-lg cursor-pointer"
            side={isMobile ? "bottom" : "right"}
            align="end"
            sideOffset={4}
          >
            <DropdownMenuLabel className="p-0 font-normal">
              <div className="flex items-center gap-2 px-1 py-1.5 text-left text-sm cursor-auto">
                <Avatar className="h-8 w-8 rounded-lg">
                  <AvatarImage src={avatarUrl} alt={`${firstName} ${lastName}`} />
                  <AvatarFallback className="rounded-lg">
                    {firstName?.[0]}
                    {lastName?.[0]}
                  </AvatarFallback>
                </Avatar>
                <div className="grid flex-1 text-left text-sm leading-tight">
                  <span className="truncate font-medium">{`${firstName} ${lastName}`}</span>
                  <span className="truncate text-xs">{email}</span>
                  {roleLabel && <span className="truncate text-xs">{roleLabel}</span>}
                </div>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuGroup>
              <DropdownMenuItem
                className="cursor-pointer"
                onSelect={() => {
                  navigator.clipboard.writeText(email);
                  toast.success("E-mail copié dans le presse-papiers !");
                }}
              >
                <Copy />
                Copier mon e-mail
              </DropdownMenuItem>
              <DropdownMenuItem
                className="cursor-pointer"
                onClick={() => navigate(helpEntryPath)}
              >
                <Headset />
                Centre d'aide
              </DropdownMenuItem>
            </DropdownMenuGroup>
            <DropdownMenuGroup>
              {servicePreferences && (
                <DropdownMenuItem
                  className="cursor-pointer"
                  onClick={() => navigate(USER_SETTINGS_PATH)}
                >
                  <Settings />
                  Mes préférences
                </DropdownMenuItem>
              )}
            </DropdownMenuGroup>
            <DropdownMenuSeparator />
            <ThemeSwitcher />
            <DropdownMenuSeparator />
            <DropdownMenuItem
              className="cursor-pointer"
              onClick={() => dispatch(logout({ isVoluntary: true }))}
            >
              <LogOut />
              Se déconnecter
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>
  );
};

export default UserBadge;
